import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, Row, Select, Space } from "antd";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import AddressMaker, { AddressType } from "./AddressMaker";

import * as String from "../commons/string";
import * as Types from "../commons/typeDTO";
import * as Request from "../commons/request";
import * as Common from "../commons/common";
import dayjs from "dayjs";

function RentRegisterB2b({
    editMode,
    b2b,
    onFinish,
}: {
    editMode: string;
    b2b?: Types.B2bDto;
    onFinish?: (b2b: Types.B2bDto, fullAddress?: AddressType) => void;
}) {
    const [form] = Form.useForm();

    const [address, setAddress] = useState<AddressType>();
    const [coupons, setCoupons] = useState<Array<Types.CouponDto>>();
    const [users, setUsers] = useState<Array<Types.UserDto>>();
    const [rentCars, setRentCars] = useState<Array<Types.RentCarDto>>();

    useEffect(() => {
        requestGetCoupons();
        requestGetUsers();
        requestGetRentCars();
    }, []);

    useEffect(() => {
        if (b2b) {
            form.setFieldsValue(b2b);

            setAddress({
                address: b2b.address,
                addressDetails: b2b.addressDetails,
            });

            form.setFieldsValue({
                fullAddress: {
                    address: b2b.address ?? "",
                    addressDetails: b2b.addressDetails ?? "",
                },
                managers: b2b?.managers?.map((manager) => manager.email),
                rentCars: b2b?.rentCars?.map((car) => car.rentCarId),
            });
        } else {
            form.resetFields();
        }
    }, [b2b]);

    //get coupons
    const requestAxiosGetCoupons = async () => {
        const response = await utilAxiosWithAuth().get(Request.COUPONS_URL, { params: { couponCoverage: "B2B" } });
        return response.data;
    };

    const { loading: loadingGetCoupons, error: errorGetCoupons, data: resultGetCoupons, execute: requestGetCoupons } = useAsyncAxios(requestAxiosGetCoupons);

    useEffect(() => {
        if (resultGetCoupons === null) return;
        console.log("resultGetCoupons", resultGetCoupons);

        setCoupons(resultGetCoupons.coupons?.filter((coupon: Types.CouponDto) => dayjs().isBefore(coupon.expiredDate)));
    }, [resultGetCoupons]);

    useEffect(() => {
        if (errorGetCoupons === null) return;

        console.log("errorGetCoupons", errorGetCoupons);
    }, [errorGetCoupons]);

    //get users
    const requestAxiosGetUsers = async () => {
        const response = await utilAxiosWithAuth().get(Request.USERS_URL, { params: { authorityNames: Common.MEMBER_ROLE.ROLE_B2B_MANAGER } });
        return response.data;
    };
    const { loading: loadingGetUsers, error: errorGetUsers, data: resultGetUsers, execute: requestGetUsers } = useAsyncAxios(requestAxiosGetUsers);

    useEffect(() => {
        if (resultGetUsers === null) return;

        console.log("resultGetUsers", resultGetUsers.users.content);
        setUsers(resultGetUsers.users.content);
    }, [resultGetUsers]);

    useEffect(() => {
        if (errorGetUsers === null) return;

        console.log("errorGetUsers", errorGetUsers);
    }, [errorGetUsers]);

    //get rentCars
    const requestAxiosGetRentCars = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_CARS_ALL_URL);
        return response.data;
    };
    const {
        loading: loadingGetRentCars,
        error: errorGetRentCars,
        data: resultGetRentCars,
        execute: requestGetRentCars,
    } = useAsyncAxios(requestAxiosGetRentCars);

    useEffect(() => {
        if (resultGetRentCars === null) return;

        console.log("resultGetRentCars", resultGetRentCars.rentCars);
        setRentCars(resultGetRentCars.rentCars);
    }, [resultGetRentCars]);

    useEffect(() => {
        if (errorGetRentCars === null) return;

        console.log("errorGetRentCars", errorGetRentCars);
    }, [errorGetRentCars]);

    return (
        <Form form={form} onFinish={(value) => onFinish && onFinish(value, address)}>
            <Space size={20} direction="vertical" style={{ width: "100%" }}>
                <Row>
                    <Col span={12}>
                        <Form.Item
                            className="searchForm"
                            name="b2bName"
                            label="회사/단체명"
                            rules={[
                                {
                                    required: true,
                                    message: "회사/단체명을 입력해 주세요.",
                                },
                            ]}
                        >
                            <Input placeholder="회사/단체명" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item className="searchForm" name="promotionCode" label="프로모션코드">
                            <Input disabled placeholder="프로모션코드" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item className="searchForm" name="fullAddress" label="주소">
                            <AddressMaker value={address} onChangeAddress={(address) => setAddress(address)} />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item className="searchForm" name="rentCars" label="차량">
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                optionFilterProp="label"
                                options={rentCars?.map((car) => ({
                                    value: car?.rentCarId,
                                    label:
                                        car?.car?.carNumber +
                                        (car?.title?.includes("전용") ? ` ${car?.title?.replace("[", "(").substring(0, car?.title?.indexOf("]"))})` : ""),
                                }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item className="searchForm" name={["coupon", "couponId"]} label="단체 쿠폰">
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                options={coupons?.map((coupon) => ({ value: coupon?.couponId, label: coupon?.couponName }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item className="searchForm" name="managers" label="관리자">
                            <Select
                                allowClear
                                mode="multiple"
                                options={users?.map((user: Types.UserDto) => ({
                                    label: `${user?.name} (${user.email})`,
                                    value: user?.email,
                                }))}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row justify="end">
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {editMode === "add" ? String.register : String.update}
                        </Button>
                    </Form.Item>
                </Row>
            </Space>
        </Form>
    );
}

export default RentRegisterB2b;
